import React, { FC, useState, useMemo, useRef, useEffect } from "react";
import classNames from "classnames";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import ContentHeader from "../../components/Admin/ContentHeader/ContentHeader";
import { Button } from "../../components/Admin/Button/Button";
import { CustomTextField } from "../../components/Admin/CustomTextField/CustomTextField";
import {
  AdminAPI,
  BranchAPI,
  CategoriesTopicsTableAPI,
} from "../../constants/API";
import { commonHeaders } from "../../constants/HeadersAPI";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { getUserData } from "../../utils/Authorization/authorization";
import classes from "./BranchesPage.module.scss";
import modalBase from "../../components/ModalBase/ModalBase.module.scss";

export type BranchDto = {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
};

export type BranchQueueItemDto = {
  id: number;
  queueName: string;
  terminalName: string;
  monitorName: string;
};

export type BranchQueueCreatedDto = {
  terminalUrl: string;
  tabloUrl: string;
  operatorUrl: string;
  adminUrl: string;
  terminalToken: string;
  monitorToken: string;
};

export type TabloLinkItem = {
  tabloUrl: string;
  monitorToken: string;
  monitorName: string;
  monitorId: number;
  tabloOrientation?: "VERTICAL" | "HORIZONTAL" | null;
};

export type TerminalLinkItem = {
  terminalUrl: string;
  terminalToken: string;
  terminalName: string;
  orderUnitId: number;
  ipAddress: string | null;
  terminalOrientation?: "VERTICAL" | "HORIZONTAL" | null;
};

export type RatingLinkItem = {
  ratingUrl: string;
  serviceUnitId: number;
  serviceUnitName: string;
  cabinet: number | null;
  window: number | null;
};

export type BranchQueueLinksDto = {
  operatorUrl: string;
  adminUrl: string;
  terminals: TerminalLinkItem[];
  tablos: TabloLinkItem[];
  ratingLinks?: RatingLinkItem[];
};

export type QueueMonitorAddedDto = {
  tabloUrl: string;
  monitorToken: string;
  monitorId: number;
  monitorName: string;
};

export type QueueTerminalAddedDto = {
  terminalUrl: string;
  terminalToken: string;
  orderUnitId: number;
  terminalName: string;
};

/** Ссылку с подставленным origin текущего приложения (чтобы открывался фронтенд, а не бэкенд). */
function toFrontendLink(url: string): string {
  if (typeof window === "undefined" || !url) return url;
  try {
    const u = new URL(url);
    return `${window.location.origin}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

function isValidIpWithOptionalPort(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  const ipPortRegex =
    /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})(?::([0-9]{1,5}))?$/;
  const match = trimmed.match(ipPortRegex);
  if (!match) return false;
  if (match[1] != null) {
    const port = Number(match[1]);
    if (port < 1 || port > 65535) return false;
  }
  return true;
}

function getMutationError(err: unknown): string | false {
  return (
    (err &&
      (axios.isAxiosError(err)
        ? err.response?.data?.message || err.message
        : "Ошибка")) ||
    false
  );
}

const BranchesPage: FC = () => {
  const currentUser = getUserData() as
    | {
        id?: number;
        role?: string;
      }
    | string;
  const userRoleRaw =
    typeof currentUser !== "string" ? (currentUser?.role ?? "") : "";
  const userRole = userRoleRaw.trim();
  const currentUserId =
    typeof currentUser !== "string" && typeof currentUser?.id === "number"
      ? currentUser.id
      : null;
  const isSuperAdmin = userRole === "Суперадминистратор Tehnosky";
  const isBranchAdmin = userRole.startsWith("Администратор");
  const { canEdit: canEditPermission, canView: canViewPermission } =
    useMyPermissions();
  const canEditBranches = canEditPermission("BRANCHES_EDIT");
  const canViewServices =
    isSuperAdmin ||
    canViewPermission("SERVICES_VIEW") ||
    canEditPermission("SERVICES_CREATE") ||
    canEditPermission("SERVICES_EDIT") ||
    canEditPermission("SERVICES_DELETE") ||
    canEditPermission("SERVICES_ACTIVITY");
  const queryClient = useQueryClient();
  const [modalBranch, setModalBranch] = useState(false);
  const [modalQueue, setModalQueue] = useState(false);
  const [modalEditBranch, setModalEditBranch] = useState<BranchDto | null>(
    null,
  );
  const [modalEditQueue, setModalEditQueue] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
  } | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<BranchDto | null>(null);
  const [selectedBranchForQueues, setSelectedBranchForQueues] =
    useState<BranchDto | null>(null);
  const [queueResult, setQueueResult] = useState<BranchQueueCreatedDto | null>(
    null,
  );
  const [deleteQueueConfirm, setDeleteQueueConfirm] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
  } | null>(null);
  const [deleteBranchConfirm, setDeleteBranchConfirm] =
    useState<BranchDto | null>(null);
  const [queueLinksTarget, setQueueLinksTarget] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
  } | null>(null);
  const [addTabloTarget, setAddTabloTarget] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
  } | null>(null);
  const [addTabloResult, setAddTabloResult] =
    useState<QueueMonitorAddedDto | null>(null);
  const [addTerminalTarget, setAddTerminalTarget] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
  } | null>(null);
  const [addTerminalResult, setAddTerminalResult] =
    useState<QueueTerminalAddedDto | null>(null);
  const [deleteTabloConfirm, setDeleteTabloConfirm] = useState<{
    tablo: TabloLinkItem;
    branchId: number;
    queueId: number;
  } | null>(null);
  const [deleteTerminalConfirm, setDeleteTerminalConfirm] = useState<{
    terminal: TerminalLinkItem;
    branchId: number;
    queueId: number;
  } | null>(null);
  const [editTerminalIpTarget, setEditTerminalIpTarget] = useState<{
    terminal: TerminalLinkItem;
    branchId: number;
    queueId: number;
  } | null>(null);
  const [bindingModal, setBindingModal] = useState<{
    branch: BranchDto;
    queue: BranchQueueItemDto;
    type: "categories" | "topics" | "monitor-topics";
  } | null>(null);
  const [createBranchValidationError, setCreateBranchValidationError] =
    useState<string | null>(null);
  const [editBranchValidationError, setEditBranchValidationError] = useState<
    string | null
  >(null);

  const validateBranchName = (rawName: string, currentBranchId?: number) => {
    const trimmed = rawName.trim();
    if (!rawName) {
      return 'Поле "Название" не может быть пустым';
    }
    if (!trimmed) {
      return "Название филиала не может состоять только из пробелов";
    }
    const duplicate = branches.some(
      (b) =>
        b.isActive !== false &&
        b.name.trim() === trimmed &&
        (currentBranchId == null || b.id !== currentBranchId),
    );
    if (duplicate) {
      return "Филиал с таким названием уже существует";
    }
    return null;
  };

  const { data: branches = [], isLoading } = useQuery<BranchDto[]>(
    "branches",
    () => axios.get(BranchAPI.getAll, commonHeaders()).then((r) => r.data),
    { enabled: !!localStorage.getItem("user") },
  );

  // Системный "default" филиал не должен отображаться ни в каких списках.
  const DEFAULT_BRANCH = "default";
  const defaultLower = DEFAULT_BRANCH.toLowerCase();
  const activeBranches = branches.filter(
    (b) =>
      b.isActive !== false &&
      b.id !== 0 &&
      b.code.trim().toLowerCase() !== defaultLower &&
      b.name.trim().toLowerCase() !== defaultLower,
  );

  const { data: currentUserBranchIds = [] } = useQuery<number[]>(
    ["userBranches", currentUserId],
    () =>
      axios
        .get(AdminAPI.getUserBranches(currentUserId as number), commonHeaders())
        .then((r) => r.data),
    {
      enabled: isBranchAdmin && !!currentUserId,
    },
  );

  const { data: currentUserQueueIds = [] } = useQuery<number[]>(
    ["userQueues", currentUserId],
    () =>
      axios
        .get(AdminAPI.getUserQueues(currentUserId as number), commonHeaders())
        .then((r) => r.data),
    {
      enabled: isBranchAdmin && !!currentUserId,
    },
  );

  const { data: currentUserQueues = [] } = useQuery<BranchQueueItemDto[]>(
    ["branches", "queues", "for-current-user"],
    () =>
      axios
        .get<
          BranchQueueItemDto[]
        >(BranchAPI.getQueuesForCurrentUser, commonHeaders())
        .then((r) => r.data),
    {
      enabled: isBranchAdmin,
    },
  );

  const visibleBranches = useMemo(() => {
    if (!isBranchAdmin) return activeBranches;
    if (currentUserBranchIds.length === 0) return [];
    const allowedBranchIds = new Set(currentUserBranchIds);
    return activeBranches.filter((branch) => allowedBranchIds.has(branch.id));
  }, [activeBranches, currentUserBranchIds, isBranchAdmin]);

  const { data: queues = [], isLoading: queuesLoading } = useQuery<
    BranchQueueItemDto[]
  >(
    ["branches", selectedBranchForQueues?.id, "queues"],
    () =>
      selectedBranchForQueues
        ? axios
            .get(
              BranchAPI.getQueues(selectedBranchForQueues.id),
              commonHeaders(),
            )
            .then((r) => r.data)
        : Promise.resolve([]),
    { enabled: !!selectedBranchForQueues },
  );

  const visibleQueues = useMemo(() => {
    if (!isBranchAdmin) return queues;
    const queueIdsFromUserQueues = currentUserQueues.map((q) => q.id);
    const mergedAllowedQueueIds = Array.from(
      new Set([...currentUserQueueIds, ...queueIdsFromUserQueues]),
    );
    if (mergedAllowedQueueIds.length === 0) return [];
    const allowedQueueIds = new Set(mergedAllowedQueueIds);
    return queues.filter((queue) => allowedQueueIds.has(queue.id));
  }, [queues, currentUserQueueIds, currentUserQueues, isBranchAdmin]);

  useEffect(() => {
    if (!selectedBranchForQueues) return;
    if (visibleBranches.some((b) => b.id === selectedBranchForQueues.id))
      return;
    setSelectedBranchForQueues(null);
  }, [selectedBranchForQueues, visibleBranches]);

  const {
    data: queueLinks,
    isLoading: queueLinksLoading,
    error: queueLinksError,
  } = useQuery<BranchQueueLinksDto>(
    [
      "branchQueueLinks",
      queueLinksTarget?.branch.id,
      queueLinksTarget?.queue.id,
    ],
    () =>
      queueLinksTarget
        ? axios
            .get(
              BranchAPI.queueLinks(
                queueLinksTarget.branch.id,
                queueLinksTarget.queue.id,
              ),
              commonHeaders(),
            )
            .then((r) => r.data)
        : Promise.reject(new Error("no target")),
    { enabled: !!queueLinksTarget },
  );

  const createBranchMutation = useMutation(
    (body: { code: string; name: string }) =>
      axios.post(BranchAPI.create, body, commonHeaders()),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("branches");
        setModalBranch(false);
      },
    },
  );

  const updateBranchMutation = useMutation(
    (args: { branchId: number; body: { code: string; name: string } }) =>
      axios.put(BranchAPI.update(args.branchId), args.body, commonHeaders()),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries("branches");
        if (selectedBranchForQueues?.id === vars.branchId) {
          queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        }
        setModalEditBranch(null);
      },
    },
  );

  const createQueueMutation = useMutation(
    (args: {
      branchId: number;
      body: {
        queueName?: string;
        terminalName?: string;
        monitorName?: string;
        ipAddress?: string;
        terminalOrientation?: "VERTICAL" | "HORIZONTAL";
        tabloOrientation?: "VERTICAL" | "HORIZONTAL";
      };
    }) =>
      axios.post(
        BranchAPI.createQueue(args.branchId),
        args.body,
        commonHeaders(),
      ),
    {
      onSuccess: (res, vars) => {
        setQueueResult(res.data);
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
      },
    },
  );

  const updateQueueMutation = useMutation(
    (args: {
      branchId: number;
      queueId: number;
      body: {
        queueName?: string;
        tabloOrientation?: "VERTICAL" | "HORIZONTAL";
      };
    }) =>
      axios.put(
        BranchAPI.updateQueue(args.branchId, args.queueId),
        args.body,
        commonHeaders(),
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        setModalEditQueue(null);
      },
    },
  );

  const deleteQueueMutation = useMutation(
    (args: { branchId: number; queueId: number }) =>
      axios.delete(
        BranchAPI.deleteQueue(args.branchId, args.queueId),
        commonHeaders(),
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        // Очередь влияет на отображение связанных сущностей в админ-таблицах (в т.ч. "Рабочие места").
        // После удаления очереди нужно сбросить кэш, чтобы не показывать устаревшие привязки.
        queryClient.invalidateQueries("workingPlaces");
        setDeleteQueueConfirm(null);
      },
    },
  );

  const addQueueMonitorMutation = useMutation(
    (args: {
      branchId: number;
      queueId: number;
      body?: { monitorName?: string };
    }) =>
      axios.post<QueueMonitorAddedDto>(
        BranchAPI.addQueueMonitor(args.branchId, args.queueId),
        args.body ?? {},
        commonHeaders(),
      ),
    {
      onSuccess: (res, vars) => {
        setAddTabloResult(res.data);
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        if (
          queueLinksTarget &&
          queueLinksTarget.branch.id === vars.branchId &&
          queueLinksTarget.queue.id === vars.queueId
        ) {
          queryClient.invalidateQueries([
            "branchQueueLinks",
            vars.branchId,
            vars.queueId,
          ]);
        }
      },
    },
  );

  const deleteQueueMonitorMutation = useMutation(
    (args: { branchId: number; queueId: number; monitorId: number }) =>
      axios.delete(
        BranchAPI.deleteQueueMonitor(
          args.branchId,
          args.queueId,
          args.monitorId,
        ),
        commonHeaders(),
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        queryClient.invalidateQueries([
          "branchQueueLinks",
          vars.branchId,
          vars.queueId,
        ]);
        setDeleteTabloConfirm(null);
      },
    },
  );

  const addQueueTerminalMutation = useMutation(
    (args: {
      branchId: number;
      queueId: number;
      body?: { terminalName?: string; ipAddress?: string };
    }) =>
      axios
        .post<QueueTerminalAddedDto>(
          BranchAPI.addQueueTerminal(args.branchId, args.queueId),
          args.body ?? {},
          commonHeaders(),
        )
        .then((r) => r.data),
    {
      onSuccess: (data, vars) => {
        setAddTerminalResult(data);
        queryClient.invalidateQueries([
          "branchQueueLinks",
          vars.branchId,
          vars.queueId,
        ]);
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
      },
    },
  );
  const updateTerminalIpMutation = useMutation(
    (args: {
      branchId: number;
      queueId: number;
      orderUnitId: number;
      ipAddress: string | null;
    }) =>
      axios.patch(
        BranchAPI.updateTerminalIp(
          args.branchId,
          args.queueId,
          args.orderUnitId,
        ),
        { ipAddress: args.ipAddress },
        commonHeaders(),
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries([
          "branchQueueLinks",
          vars.branchId,
          vars.queueId,
        ]);
        setEditTerminalIpTarget(null);
      },
    },
  );
  const deleteQueueTerminalMutation = useMutation(
    (args: { branchId: number; queueId: number; orderUnitId: number }) =>
      axios.delete(
        BranchAPI.deleteQueueTerminal(
          args.branchId,
          args.queueId,
          args.orderUnitId,
        ),
        commonHeaders(),
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries([
          "branchQueueLinks",
          vars.branchId,
          vars.queueId,
        ]);
        queryClient.invalidateQueries(["branches", vars.branchId, "queues"]);
        setDeleteTerminalConfirm(null);
        if (
          queueLinksTarget &&
          queueLinksTarget.branch.id === vars.branchId &&
          queueLinksTarget.queue.id === vars.queueId
        ) {
          setQueueLinksTarget(null);
        }
      },
    },
  );

  const deleteBranchMutation = useMutation(
    (args: { branchId: number }) =>
      axios.delete(BranchAPI.delete(args.branchId), commonHeaders()),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries("branches");
        if (selectedBranchForQueues?.id === vars.branchId) {
          setSelectedBranchForQueues(null);
        }
        setDeleteBranchConfirm(null);
      },
    },
  );

  const openCreateQueue = (branch: BranchDto) => {
    setSelectedBranch(branch);
    setQueueResult(null);
    setModalQueue(true);
  };

  return (
    <div className={classes.container}>
      <ContentHeader
        name="Филиалы"
        handleButtonClick={
          isSuperAdmin ? () => setModalBranch(true) : undefined
        }
      />
      <div className={classes.tableWrap}>
        {isLoading ? (
          <div style={{ padding: 24 }}>Загрузка...</div>
        ) : visibleBranches.length === 0 ? (
          <p className={classes.queuesEmpty}>
            {isSuperAdmin
              ? "Нет созданных филиалов."
              : "Нет привязанных филиалов."}
          </p>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleBranches.map((b) => (
                <tr key={b.id}>
                  <td>{b.name}</td>
                  <td className={classes.actionsCell}>
                    {isSuperAdmin ? (
                      <>
                        <button
                          type="button"
                          className={classes.btnSmall}
                          onClick={() => setModalEditBranch(b)}
                        >
                          Изменить
                        </button>
                        {/* <button
                          type="button"
                          className={classes.btnQueue}
                          onClick={() => openCreateQueue(b)}
                        >
                          Создать очередь
                        </button> */}
                        <button
                          type="button"
                          className={classes.btnSmall}
                          onClick={() =>
                            setSelectedBranchForQueues(
                              selectedBranchForQueues?.id === b.id ? null : b,
                            )
                          }
                        >
                          {selectedBranchForQueues?.id === b.id
                            ? "Скрыть очереди"
                            : "Очереди"}
                        </button>
                        <button
                          type="button"
                          className={classes.btnDanger}
                          onClick={() => setDeleteBranchConfirm(b)}
                        >
                          Удалить
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={classes.btnSmall}
                        onClick={() =>
                          setSelectedBranchForQueues(
                            selectedBranchForQueues?.id === b.id ? null : b,
                          )
                        }
                      >
                        {selectedBranchForQueues?.id === b.id
                          ? "Скрыть очереди"
                          : "Очереди"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedBranchForQueues && (
        <div className={classes.queuesSection}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <h3 className={classes.queuesSectionTitle} style={{ margin: 0 }}>
              Очереди филиала «{selectedBranchForQueues.name}»
            </h3>
            {isSuperAdmin && (
              <button
                type="button"
                className={classes.btnQueue}
                onClick={() => {
                  setSelectedBranch(selectedBranchForQueues);
                  setQueueResult(null);
                  setModalQueue(true);
                }}
              >
                Добавить очередь
              </button>
            )}
          </div>
          {queuesLoading ? (
            <div style={{ padding: 16 }}>Загрузка очередей...</div>
          ) : visibleQueues.length === 0 ? (
            <p className={classes.queuesEmpty}>
              {isBranchAdmin
                ? "Нет привязанных очередей."
                : "Нет очередей. Нажмите на кнопку «Добавить очередь»."}
            </p>
          ) : (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Очередь</th>
                  <th>Терминал</th>
                  <th>Табло</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {visibleQueues.map((q) => (
                  <tr key={q.id}>
                    <td>{q.queueName}</td>
                    <td>{q.terminalName}</td>
                    <td>{q.monitorName}</td>
                    <td className={classes.actionsCell}>
                      {(isSuperAdmin || canEditBranches) && (
                        <>
                          {canViewServices && (
                            <>
                              {/* <button
                                type="button"
                                className={classes.btnSmall}
                                onClick={() =>
                                  setBindingModal({
                                    branch: selectedBranchForQueues,
                                    queue: q,
                                    type: "categories",
                                  })
                                }
                              >
                                Категории
                              </button> */}
                              {/* <button
                                type="button"
                                className={classes.btnSmall}
                                onClick={() =>
                                  setBindingModal({
                                    branch: selectedBranchForQueues,
                                    queue: q,
                                    type: "topics",
                                  })
                                }
                              >
                                Услуги на терминале
                              </button> */}
                            </>
                          )}
                          <button
                            type="button"
                            className={classes.btnSmall}
                            onClick={() =>
                              setQueueLinksTarget({
                                branch: selectedBranchForQueues,
                                queue: q,
                              })
                            }
                          >
                            Ссылки
                          </button>
                        </>
                      )}
                      {(isSuperAdmin || isBranchAdmin || canEditBranches) && (
                        <>
                          {canViewServices && (
                            <button
                              type="button"
                              className={classes.btnSmall}
                              onClick={() =>
                                setBindingModal({
                                  branch: selectedBranchForQueues,
                                  queue: q,
                                  type: "monitor-topics",
                                })
                              }
                            >
                              Услуги на табло
                            </button>
                          )}
                          <button
                            type="button"
                            className={classes.btnSmall}
                            onClick={() =>
                              setModalEditQueue({
                                branch: selectedBranchForQueues,
                                queue: q,
                              })
                            }
                          >
                            Изменить
                          </button>
                        </>
                      )}
                      {isSuperAdmin && (
                        <>
                          <button
                            type="button"
                            className={classes.btnSmall}
                            onClick={() => {
                              setAddTabloResult(null);
                              setAddTabloTarget({
                                branch: selectedBranchForQueues,
                                queue: q,
                              });
                            }}
                          >
                            Добавить табло
                          </button>
                          <button
                            type="button"
                            className={classes.btnSmall}
                            onClick={() => {
                              setAddTerminalResult(null);
                              setAddTerminalTarget({
                                branch: selectedBranchForQueues,
                                queue: q,
                              });
                            }}
                          >
                            Добавить терминал
                          </button>
                          <button
                            type="button"
                            className={classes.btnDanger}
                            onClick={() =>
                              setDeleteQueueConfirm({
                                branch: selectedBranchForQueues,
                                queue: q,
                              })
                            }
                          >
                            Удалить
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modalBranch && (
        <ModalCreateBranch
          onClose={() => {
            setCreateBranchValidationError(null);
            (createBranchMutation as any).reset?.();
            setModalBranch(false);
          }}
          onSubmit={(code, name) => {
            const error = validateBranchName(name);
            if (error) {
              setCreateBranchValidationError(error);
              return;
            }
            setCreateBranchValidationError(null);
            createBranchMutation.mutate({
              code: code.trim(),
              name: name.trim(),
            });
          }}
          isLoading={createBranchMutation.isLoading}
          error={
            createBranchValidationError ||
            (getMutationError(createBranchMutation.error) as string | false)
          }
        />
      )}

      {modalEditBranch && (
        <ModalEditBranch
          branch={modalEditBranch}
          onClose={() => {
            setEditBranchValidationError(null);
            (updateBranchMutation as any).reset?.();
            setModalEditBranch(null);
          }}
          onSubmit={(code, name) => {
            const error = validateBranchName(name, modalEditBranch.id);
            if (error) {
              setEditBranchValidationError(error);
              return;
            }
            setEditBranchValidationError(null);
            updateBranchMutation.mutate({
              branchId: modalEditBranch.id,
              body: { code: code.trim(), name: name.trim() },
            });
          }}
          isLoading={updateBranchMutation.isLoading}
          error={
            editBranchValidationError ||
            (getMutationError(updateBranchMutation.error) as string | false)
          }
        />
      )}

      {modalQueue && selectedBranch && (
        <ModalCreateQueue
          branchId={selectedBranch.id}
          branchName={selectedBranch.name}
          onClose={() => {
            (createQueueMutation as any).reset?.();
            setModalQueue(false);
            setSelectedBranch(null);
            setQueueResult(null);
          }}
          onSubmit={(body) => {
            createQueueMutation.mutate({ branchId: selectedBranch.id, body });
          }}
          isLoading={createQueueMutation.isLoading}
          queueResult={queueResult}
          error={getMutationError(createQueueMutation.error) as string | false}
        />
      )}

      {modalEditQueue && (
        <ModalEditQueue
          branchId={modalEditQueue.branch.id}
          branchName={modalEditQueue.branch.name}
          queue={modalEditQueue.queue}
          onClose={() => setModalEditQueue(null)}
        />
      )}

      {deleteQueueConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteQueueMutation as any).reset?.();
            setDeleteQueueConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <h2 className={modalBase.title}>Удалить очередь?</h2>
            <p>
              Очередь «{deleteQueueConfirm.queue.queueName}» (терминал:{" "}
              {deleteQueueConfirm.queue.terminalName}) будет удалена без
              возможности восстановления.
            </p>
            {deleteQueueMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteQueueMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => {
                  (deleteQueueMutation as any).reset?.();
                  setDeleteQueueConfirm(null);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className={classes.btnDanger}
                disabled={deleteQueueMutation.isLoading}
                onClick={() =>
                  deleteQueueMutation.mutate({
                    branchId: deleteQueueConfirm.branch.id,
                    queueId: deleteQueueConfirm.queue.id,
                  })
                }
              >
                {deleteQueueMutation.isLoading ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteBranchConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteBranchMutation as any).reset?.();
            setDeleteBranchConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <h2 className={modalBase.title}>Удалить филиал?</h2>
            <p>
              Филиал «{deleteBranchConfirm.name}» и все связанные с ним данные
              будут полностью удалены из БД. Это действие необратимо.
            </p>
            {deleteBranchMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteBranchMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => {
                  (deleteBranchMutation as any).reset?.();
                  setDeleteBranchConfirm(null);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className={classes.btnDanger}
                disabled={deleteBranchMutation.isLoading}
                onClick={() =>
                  deleteBranchMutation.mutate({
                    branchId: deleteBranchConfirm.id,
                  })
                }
              >
                {deleteBranchMutation.isLoading ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bindingModal && (
        <ModalQueueBinding
          branchId={bindingModal.branch.id}
          branchCode={bindingModal.branch.code}
          queueId={bindingModal.queue.id}
          type={bindingModal.type}
          title={`${bindingModal.type === "categories" ? "Категории" : bindingModal.type === "topics" ? "Услуги на терминале" : "Услуги на табло"} — ${bindingModal.queue.queueName}`}
          onClose={() => setBindingModal(null)}
          onSaved={() => {
            queryClient.invalidateQueries([
              "branches",
              bindingModal.branch.id,
              "queues",
            ]);
            setBindingModal(null);
          }}
        />
      )}

      {addTabloTarget && (
        <ModalAddTablo
          branchName={addTabloTarget.branch.name}
          queueName={addTabloTarget.queue.queueName}
          onClose={() => {
            (addQueueMonitorMutation as any).reset?.();
            setAddTabloTarget(null);
            setAddTabloResult(null);
          }}
          onSubmit={(body) => {
            addQueueMonitorMutation.mutate({
              branchId: addTabloTarget.branch.id,
              queueId: addTabloTarget.queue.id,
              body,
            });
          }}
          isLoading={addQueueMonitorMutation.isLoading}
          result={addTabloResult}
          error={
            getMutationError(addQueueMonitorMutation.error) as string | false
          }
        />
      )}

      {addTerminalTarget && (
        <ModalAddTerminal
          branchName={addTerminalTarget.branch.name}
          queueName={addTerminalTarget.queue.queueName}
          onClose={() => {
            (addQueueTerminalMutation as any).reset?.();
            setAddTerminalTarget(null);
            setAddTerminalResult(null);
          }}
          onSubmit={(body) => {
            addQueueTerminalMutation.mutate({
              branchId: addTerminalTarget.branch.id,
              queueId: addTerminalTarget.queue.id,
              body,
            });
          }}
          isLoading={addQueueTerminalMutation.isLoading}
          result={addTerminalResult}
          error={
            getMutationError(addQueueTerminalMutation.error) as string | false
          }
        />
      )}

      {queueLinksTarget && (
        <div
          className={classes.modalOverlay}
          onClick={() => setQueueLinksTarget(null)}
        >
          <div
            className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520 }}
          >
            <h2 className={classes.modalTitle}>Ссылки очереди</h2>
            <div className={classes.modalFormScrollable}>
              <div className={classes.modalScrollArea}>
                <p style={{ marginTop: 0 }}>
                  {queueLinksTarget.branch.name} —{" "}
                  {queueLinksTarget.queue.queueName}
                </p>
                {queueLinksLoading ? (
                  <div style={{ padding: 12 }}>Загрузка...</div>
                ) : queueLinksError ? (
                  <div className={classes.error}>
                    {getMutationError(queueLinksError)}
                  </div>
                ) : queueLinks ? (
                  <div className={classes.urlsBlock}>
                    {queueLinks.terminals && queueLinks.terminals.length > 0 ? (
                      <>
                        <p style={{ marginBottom: 4 }}>
                          <strong>Терминалы:</strong>
                        </p>
                        {queueLinks.terminals.map((t, i) => (
                          <div
                            key={t.orderUnitId}
                            style={{
                              marginTop: 8,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {queueLinks.terminals.length > 1 ? (
                                <strong>
                                  {t.terminalName || `Терминал ${i + 1}`}:
                                </strong>
                              ) : null}
                              {queueLinks.terminals.length > 1 && <br />}
                              <a
                                href={toFrontendLink(t.terminalUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {toFrontendLink(t.terminalUrl)}
                              </a>
                              <br />
                              <span style={{ fontSize: "0.9em" }}>
                                Token: {t.terminalToken}
                                {t.terminalOrientation && (
                                  <>
                                    {" "}
                                    (ориентация:{" "}
                                    {t.terminalOrientation === "VERTICAL"
                                      ? "вертикальная"
                                      : "горизонтальная"}
                                    )
                                  </>
                                )}
                              </span>
                              {t.ipAddress != null && t.ipAddress !== "" && (
                                <>
                                  <br />
                                  <span style={{ fontSize: "0.9em" }}>
                                    IP: {t.ipAddress}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : null}
                    {queueLinks.tablos && queueLinks.tablos.length > 0 ? (
                      <>
                        <p style={{ marginBottom: 4 }}>
                          <strong>Табло:</strong>
                        </p>
                        {queueLinks.tablos.map((t, i) => (
                          <div
                            key={t.monitorId}
                            style={{
                              marginTop: 8,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {queueLinks.tablos.length > 1 ? (
                                <strong>
                                  {t.monitorName || `Табло ${i + 1}`}:
                                </strong>
                              ) : null}
                              {queueLinks.tablos.length > 1 && <br />}
                              <a
                                href={toFrontendLink(t.tabloUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {toFrontendLink(t.tabloUrl)}
                              </a>
                              <br />
                              <span style={{ fontSize: "0.9em" }}>
                                Token: {t.monitorToken}
                                {t.tabloOrientation && (
                                  <>
                                    {" "}
                                    (ориентация:{" "}
                                    {t.tabloOrientation === "VERTICAL"
                                      ? "вертикальная"
                                      : "горизонтальная"}
                                    )
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : null}
                    <p>
                      <strong>Оператор:</strong>
                      <br />
                      <a
                        href={toFrontendLink(queueLinks.operatorUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {toFrontendLink(queueLinks.operatorUrl)}
                      </a>
                    </p>
                    {queueLinks.ratingLinks && queueLinks.ratingLinks.length > 0 ? (
                      <>
                        <p style={{ marginBottom: 4 }}>
                          <strong>Оценка качества:</strong>
                        </p>
                        {queueLinks.ratingLinks.map((r) => (
                          <div
                            key={r.serviceUnitId}
                            style={{
                              marginTop: 8,
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            <strong>
                              {r.serviceUnitName || `Рабочее место ${r.serviceUnitId}`}{" "}
                              (кабинет {r.cabinet ?? "-"}, окно {r.window ?? "-"}):
                            </strong>
                            <a
                              href={toFrontendLink(r.ratingUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {toFrontendLink(r.ratingUrl)}
                            </a>
                          </div>
                        ))}
                      </>
                    ) : (
                      <p>
                        <strong>Оценка качества:</strong>
                        <br />
                        <span style={{ fontSize: "0.9em" }}>
                          Нет активных рабочих мест, привязанных к этой очереди.
                        </span>
                      </p>
                    )}
                    <p>
                      <strong>Админка:</strong>
                      <br />
                      <a
                        href={toFrontendLink(queueLinks.adminUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {toFrontendLink(queueLinks.adminUrl)}
                      </a>
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            <div
              className={`${classes.modalActions} ${classes.modalActionsSticky}`}
            >
              <Button
                content="Закрыть"
                btnStyle="blue"
                onClick={() => setQueueLinksTarget(null)}
              />
            </div>
          </div>
        </div>
      )}

      {editTerminalIpTarget && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (updateTerminalIpMutation as any).reset?.();
            setEditTerminalIpTarget(null);
          }}
        >
          <div
            className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <h2 className={classes.modalTitle}>IP-адрес терминала</h2>
            <p style={{ marginTop: 0 }}>
              {editTerminalIpTarget.terminal.terminalName || "Терминал"}
            </p>
            <EditTerminalIpForm
              initialIp={editTerminalIpTarget.terminal.ipAddress ?? ""}
              onCancel={() => {
                (updateTerminalIpMutation as any).reset?.();
                setEditTerminalIpTarget(null);
              }}
              onSubmit={(ipAddress) => {
                updateTerminalIpMutation.mutate({
                  branchId: editTerminalIpTarget.branchId,
                  queueId: editTerminalIpTarget.queueId,
                  orderUnitId: editTerminalIpTarget.terminal.orderUnitId,
                  ipAddress: ipAddress.trim() === "" ? null : ipAddress.trim(),
                });
              }}
              isLoading={updateTerminalIpMutation.isLoading}
              error={
                getMutationError(updateTerminalIpMutation.error) as
                  | string
                  | false
              }
            />
          </div>
        </div>
      )}

      {deleteTerminalConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteQueueTerminalMutation as any).reset?.();
            setDeleteTerminalConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <h2 className={modalBase.title}>Удалить терминал?</h2>
            <p>
              Терминал «
              {deleteTerminalConfirm.terminal.terminalName || "Терминал"}» будет
              удалён из очереди. У очереди должен остаться хотя бы один
              терминал.
            </p>
            {deleteQueueTerminalMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteQueueTerminalMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => {
                  (deleteQueueTerminalMutation as any).reset?.();
                  setDeleteTerminalConfirm(null);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className={classes.btnDanger}
                disabled={deleteQueueTerminalMutation.isLoading}
                onClick={() =>
                  deleteQueueTerminalMutation.mutate({
                    branchId: deleteTerminalConfirm.branchId,
                    queueId: deleteTerminalConfirm.queueId,
                    orderUnitId: deleteTerminalConfirm.terminal.orderUnitId,
                  })
                }
              >
                {deleteQueueTerminalMutation.isLoading
                  ? "Удаление..."
                  : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTabloConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteQueueMonitorMutation as any).reset?.();
            setDeleteTabloConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 400 }}
          >
            <h2 className={modalBase.title}>Удалить табло?</h2>
            <p>
              Табло «{deleteTabloConfirm.tablo.monitorName || "Табло"}» будет
              удалено из очереди. У очереди должно остаться хотя бы одно табло.
            </p>
            {deleteQueueMonitorMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteQueueMonitorMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => {
                  (deleteQueueMonitorMutation as any).reset?.();
                  setDeleteTabloConfirm(null);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className={classes.btnDanger}
                disabled={deleteQueueMonitorMutation.isLoading}
                onClick={() =>
                  deleteQueueMonitorMutation.mutate({
                    branchId: deleteTabloConfirm.branchId,
                    queueId: deleteTabloConfirm.queueId,
                    monitorId: deleteTabloConfirm.tablo.monitorId,
                  })
                }
              >
                {deleteQueueMonitorMutation.isLoading
                  ? "Удаление..."
                  : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchesPage;

type ModalCreateBranchProps = {
  onClose: () => void;
  onSubmit: (code: string, name: string) => void;
  isLoading: boolean;
  error: string | false;
};

const ModalCreateBranch: FC<ModalCreateBranchProps> = ({
  onClose,
  onSubmit,
  isLoading,
  error,
}) => {
  const [code] = useState(() => `branch-${Date.now().toString(36)}`);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const submit = () => {
    const trimmedName = name.trim();
    if (!name) {
      setNameError("Заполните это поле");
      return;
    }
    if (!trimmedName) {
      setNameError("Название филиала не может состоять только из пробелов");
      return;
    }
    if (!code.trim()) return;
    setNameError(undefined);
    onSubmit(code, name);
  };

  return (
    <div
      className={classNames(modalBase.modal, modalBase.active)}
      onClick={onClose}
    >
      <div
        className={classNames(modalBase.container, classes.modalBox)}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <h2 className={modalBase.title}>Создать филиал</h2>
        <div className={modalBase.scrollArea}>
          <div className={classes.section} style={{ marginTop: 0 }}>
            <div className={classes.deviceCard}>
              <div className={classes.deviceGrid2}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <CustomTextField
                    labelName={"Название филиала"}
                    value={name}
                    onChange={(e) => {
                      setName((e.target as HTMLInputElement).value);
                      setNameError(undefined);
                    }}
                    placeholder={"например: Филиал Минск"}
                    errorMessage={nameError}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && <div className={classes.error}>{error}</div>}
        </div>

        <div className={modalBase.footer}>
          <Button content={"отмена"} btnStyle={"white"} onClick={onClose} />
          <Button
            content={isLoading ? "создание..." : "создать"}
            btnStyle={"blue"}
            onClick={submit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

type ModalEditBranchProps = {
  branch: BranchDto;
  onClose: () => void;
  onSubmit: (code: string, name: string) => void;
  isLoading: boolean;
  error: string | false;
};

const ModalEditBranch: FC<ModalEditBranchProps> = ({
  branch,
  onClose,
  onSubmit,
  isLoading,
  error,
}) => {
  const code = branch.code;
  const [name, setName] = useState(branch.name);
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const submit = () => {
    const trimmedName = name.trim();
    if (!name) {
      setNameError("Заполните это поле");
      return;
    }
    if (!trimmedName) {
      setNameError("Название филиала не может состоять только из пробелов");
      return;
    }
    if (!code.trim()) return;
    setNameError(undefined);
    onSubmit(code, name);
  };

  return (
    <div
      className={classNames(modalBase.modal, modalBase.active)}
      onClick={onClose}
    >
      <div
        className={classNames(modalBase.container, classes.modalBox)}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <h2 className={modalBase.title}>Изменить филиал</h2>
        <div className={modalBase.scrollArea}>
          <div className={classes.section} style={{ marginTop: 0 }}>
            <div className={classes.deviceCard}>
              <div className={classes.deviceGrid2}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <CustomTextField
                    labelName={"Название филиала"}
                    value={name}
                    onChange={(e) => {
                      setName((e.target as HTMLInputElement).value);
                      setNameError(undefined);
                    }}
                    placeholder={"например: Филиал Минск"}
                    errorMessage={nameError}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && <div className={classes.error}>{error}</div>}
        </div>

        <div className={modalBase.footer}>
          <Button content={"отмена"} btnStyle={"white"} onClick={onClose} />
          <Button
            content={isLoading ? "сохранение..." : "сохранить"}
            btnStyle={"blue"}
            onClick={submit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

type ModalEditQueueProps = {
  branchId: number;
  branchName: string;
  queue: BranchQueueItemDto;
  onClose: () => void;
};

const ModalEditQueue: FC<ModalEditQueueProps> = ({
  branchId,
  branchName,
  queue,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [queueName, setQueueName] = useState(queue.queueName);
  const [tabloOrientation, setTabloOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [deleteTerminalConfirm, setDeleteTerminalConfirm] =
    useState<TerminalLinkItem | null>(null);
  const [deleteTabloConfirm, setDeleteTabloConfirm] =
    useState<TabloLinkItem | null>(null);

  const {
    data: queueLinks,
    isLoading: queueLinksLoading,
    error: queueLinksError,
  } = useQuery<BranchQueueLinksDto>(
    ["branchQueueLinks", branchId, queue.id],
    () =>
      axios
        .get(BranchAPI.queueLinks(branchId, queue.id), commonHeaders())
        .then((r) => r.data),
    { enabled: !!branchId && !!queue.id },
  );

  const [terminalIps, setTerminalIps] = useState<Record<number, string>>({});
  const [terminalIpErrors, setTerminalIpErrors] = useState<
    Record<number, string | null>
  >({});
  const [terminalNames, setTerminalNames] = useState<Record<number, string>>(
    {},
  );
  const [terminalOrientations, setTerminalOrientations] = useState<
    Record<number, "VERTICAL" | "HORIZONTAL">
  >({});
  const [monitorNames, setMonitorNames] = useState<Record<number, string>>({});
  const [monitorOrientations, setMonitorOrientations] = useState<
    Record<number, "VERTICAL" | "HORIZONTAL">
  >({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [queueNameError, setQueueNameError] = useState<string | null>(null);

  const { data: existingQueues = [] } = useQuery<BranchQueueItemDto[]>(
    ["branches", branchId, "queues", "editModal"],
    () =>
      axios
        .get(BranchAPI.getQueues(branchId), commonHeaders())
        .then((r) => r.data),
    { enabled: !!branchId },
  );

  const [initialQueueName, setInitialQueueName] = useState(queue.queueName);
  const [initialTabloOrientation, setInitialTabloOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [initialTerminalSnapshot, setInitialTerminalSnapshot] = useState<
    Record<
      number,
      {
        terminalName: string;
        ipAddress: string;
        terminalOrientation: "VERTICAL" | "HORIZONTAL";
      }
    >
  >({});
  const [initialMonitorSnapshot, setInitialMonitorSnapshot] = useState<
    Record<
      number,
      { monitorName: string; tabloOrientation: "VERTICAL" | "HORIZONTAL" }
    >
  >({});

  useEffect(() => {
    if (!queueLinks) return;
    const ips: Record<number, string> = {};
    const tnames: Record<number, string> = {};
    const tos: Record<number, "VERTICAL" | "HORIZONTAL"> = {};
    const mnames: Record<number, string> = {};
    const mos: Record<number, "VERTICAL" | "HORIZONTAL"> = {};
    const tSnap: Record<
      number,
      {
        terminalName: string;
        ipAddress: string;
        terminalOrientation: "VERTICAL" | "HORIZONTAL";
      }
    > = {};
    const mSnap: Record<
      number,
      { monitorName: string; tabloOrientation: "VERTICAL" | "HORIZONTAL" }
    > = {};
    for (const t of queueLinks.terminals ?? []) {
      ips[t.orderUnitId] = t.ipAddress ?? "";
      tnames[t.orderUnitId] = t.terminalName ?? "";
      tos[t.orderUnitId] =
        (t.terminalOrientation as "VERTICAL" | "HORIZONTAL" | null) ??
        "VERTICAL";
      tSnap[t.orderUnitId] = {
        terminalName: tnames[t.orderUnitId],
        ipAddress: ips[t.orderUnitId],
        terminalOrientation: tos[t.orderUnitId],
      };
    }
    for (const m of queueLinks.tablos ?? []) {
      mnames[m.monitorId] = m.monitorName ?? "";
      mos[m.monitorId] =
        (m.tabloOrientation as "VERTICAL" | "HORIZONTAL" | null) ??
        "HORIZONTAL";
      mSnap[m.monitorId] = {
        monitorName: mnames[m.monitorId],
        tabloOrientation: mos[m.monitorId],
      };
    }
    setTerminalIps(ips);
    setTerminalNames(tnames);
    setTerminalOrientations(tos);
    setMonitorNames(mnames);
    setMonitorOrientations(mos);
    // Если табло есть — считаем ориентацию очереди по первому табло.
    const first = queueLinks.tablos?.[0];
    if (first?.tabloOrientation) {
      const o =
        (first.tabloOrientation as "VERTICAL" | "HORIZONTAL") || "HORIZONTAL";
      setTabloOrientation(o);
      setInitialTabloOrientation(o);
    } else {
      setInitialTabloOrientation("HORIZONTAL");
    }
    setInitialQueueName(queue.queueName);
    setInitialTerminalSnapshot(tSnap);
    setInitialMonitorSnapshot(mSnap);
  }, [queueLinks]);

  const updateQueueMutation = useMutation(
    (body: {
      queueName?: string;
      tabloOrientation?: "VERTICAL" | "HORIZONTAL";
    }) =>
      axios.put(
        BranchAPI.updateQueue(branchId, queue.id),
        body,
        commonHeaders(),
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["branches", branchId, "queues"]);
      },
    },
  );

  const updateTerminalMutation = useMutation(
    (args: {
      orderUnitId: number;
      terminalName?: string | null;
      ipAddress: string | null;
      terminalOrientation?: "VERTICAL" | "HORIZONTAL" | null;
    }) =>
      axios.patch(
        BranchAPI.updateTerminalIp(branchId, queue.id, args.orderUnitId),
        {
          terminalName: args.terminalName ?? null,
          ipAddress: args.ipAddress,
          terminalOrientation: args.terminalOrientation ?? null,
        },
        commonHeaders(),
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["branchQueueLinks", branchId, queue.id]);
        queryClient.invalidateQueries(["branches", branchId, "queues"]);
      },
    },
  );

  const updateMonitorMutation = useMutation(
    (args: {
      monitorId: number;
      monitorName?: string;
      tabloOrientation?: "VERTICAL" | "HORIZONTAL";
    }) =>
      axios.patch(
        BranchAPI.updateQueueMonitor(branchId, queue.id, args.monitorId),
        {
          monitorName: args.monitorName,
          tabloOrientation: args.tabloOrientation,
        },
        commonHeaders(),
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["branchQueueLinks", branchId, queue.id]);
        queryClient.invalidateQueries(["branches", branchId, "queues"]);
      },
    },
  );

  const deleteTerminalMutation = useMutation(
    (args: { orderUnitId: number }) =>
      axios.delete(
        BranchAPI.deleteQueueTerminal(branchId, queue.id, args.orderUnitId),
        commonHeaders(),
      ),
    {
      onSuccess: async () => {
        setDeleteTerminalConfirm(null);
        await Promise.all([
          queryClient.invalidateQueries([
            "branchQueueLinks",
            branchId,
            queue.id,
          ]),
          queryClient.invalidateQueries(["branches", branchId, "queues"]),
        ]);
      },
    },
  );

  const deleteTabloMutation = useMutation(
    (args: { monitorId: number }) =>
      axios.delete(
        BranchAPI.deleteQueueMonitor(branchId, queue.id, args.monitorId),
        commonHeaders(),
      ),
    {
      onSuccess: async () => {
        setDeleteTabloConfirm(null);
        await Promise.all([
          queryClient.invalidateQueries([
            "branchQueueLinks",
            branchId,
            queue.id,
          ]),
          queryClient.invalidateQueries(["branches", branchId, "queues"]),
        ]);
      },
    },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setQueueNameError(null);

    const trimmedQueueName = queueName.trim();
    if (!trimmedQueueName) {
      setQueueNameError("Введите название очереди.");
      return;
    }

    const hasQueueDuplicate = existingQueues.some(
      (q) =>
        q.id !== queue.id &&
        q.queueName &&
        q.queueName.trim().toLowerCase() === trimmedQueueName.toLowerCase(),
    );
    if (hasQueueDuplicate) {
      setQueueNameError(
        "Очередь с таким названием уже существует в этом филиале",
      );
      return;
    }

    // Валидация IP
    const nextIpErrors: Record<number, string | null> = {};
    let hasIpError = false;
    for (const [k, v] of Object.entries(terminalIps)) {
      const id = Number(k);
      const raw = (v ?? "").trim();
      if (raw && !isValidIpWithOptionalPort(raw)) {
        nextIpErrors[id] =
          "Некорректный IP-адрес. Ожидается формат IPv4, опционально с портом.";
        hasIpError = true;
      } else {
        nextIpErrors[id] = null;
      }
    }
    setTerminalIpErrors(nextIpErrors);
    if (hasIpError) return;

    try {
      const queueBody = {
        queueName: trimmedQueueName,
        tabloOrientation,
      };

      const queueChanged =
        (queueBody.queueName ?? "") !== (initialQueueName ?? "") ||
        tabloOrientation !== initialTabloOrientation;

      const tasks: Promise<unknown>[] = [];

      if (queueChanged) {
        tasks.push(updateQueueMutation.mutateAsync(queueBody));
      }

      for (const [k] of Object.entries(terminalNames)) {
        const orderUnitId = Number(k);
        const current = {
          terminalName: (terminalNames[orderUnitId] ?? "").trim(),
          ipAddress: (terminalIps[orderUnitId] ?? "").trim(),
          terminalOrientation: terminalOrientations[orderUnitId] ?? "VERTICAL",
        };
        const initial = initialTerminalSnapshot[orderUnitId];
        if (
          !initial ||
          current.terminalName !== initial.terminalName ||
          current.ipAddress !== initial.ipAddress ||
          current.terminalOrientation !== initial.terminalOrientation
        ) {
          tasks.push(
            updateTerminalMutation.mutateAsync({
              orderUnitId,
              terminalName: current.terminalName || null,
              ipAddress: current.ipAddress === "" ? null : current.ipAddress,
              terminalOrientation: current.terminalOrientation,
            }),
          );
        }
      }

      for (const [k] of Object.entries(monitorNames)) {
        const monitorId = Number(k);
        const current = {
          monitorName: (monitorNames[monitorId] ?? "").trim(),
          tabloOrientation: monitorOrientations[monitorId] ?? "HORIZONTAL",
        };
        const initial = initialMonitorSnapshot[monitorId];
        if (
          !initial ||
          current.monitorName !== initial.monitorName ||
          current.tabloOrientation !== initial.tabloOrientation
        ) {
          tasks.push(
            updateMonitorMutation.mutateAsync({
              monitorId,
              monitorName: current.monitorName || undefined,
              tabloOrientation: current.tabloOrientation,
            }),
          );
        }
      }

      if (tasks.length > 0) {
        await Promise.all(tasks);
      }

      queryClient.invalidateQueries(["branchQueueLinks", branchId, queue.id]);
      queryClient.invalidateQueries(["branches", branchId, "queues"]);
      onClose();
    } catch (err) {
      setSaveError(getMutationError(err) || "Ошибка сохранения");
    }
  };

  return (
    <>
      <div className={classes.modalOverlay} onClick={onClose}>
        <div
          className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 820 }}
        >
          <h2 className={classes.modalTitle}>
            Изменить очередь — {branchName}
          </h2>
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={classes.modalFormScrollable}
          >
            <div className={classes.modalScrollArea}>
              <div className={classes.formRow}>
                <div className={classes.formGroup} style={{ marginBottom: 0 }}>
                  <label>Название очереди</label>
                  <input
                    value={queueName}
                    onChange={(e) => {
                      setQueueName(e.target.value);
                      if (queueNameError) setQueueNameError(null);
                    }}
                    placeholder="Название очереди"
                    className={classNames({
                      [classes.fieldInputError]: !!queueNameError,
                    })}
                  />
                  {queueNameError && (
                    <div className={classes.fieldError}>{queueNameError}</div>
                  )}
                </div>
                <div className={classes.formGroup} style={{ marginBottom: 0 }}>
                  {/* <label>Ориентация очереди</label>
                <select
                  value={tabloOrientation}
                  onChange={(e) =>
                    setTabloOrientation(
                      (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                        "HORIZONTAL",
                    )
                  }
                >
                  <option value="VERTICAL">Вертикальная</option>
                  <option value="HORIZONTAL">Горизонтальная</option>
                </select> */}
                </div>
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>Терминалы очереди</div>
                {queueLinksLoading ? (
                  <div style={{ padding: 8 }}>Загрузка...</div>
                ) : queueLinksError ? (
                  <div className={classes.error}>
                    {getMutationError(queueLinksError)}
                  </div>
                ) : queueLinks?.terminals?.length ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {queueLinks.terminals.map((t) => (
                      <div key={t.orderUnitId} className={classes.deviceCard}>
                        <div className={classes.deviceCardHeader}>
                          <div className={classes.deviceCardTitle}>
                            {t.terminalName || "Терминал"}
                          </div>
                          <Button
                            content="Удалить"
                            className={classes.btnDangerSmall}
                            isDisabled={
                              (queueLinks.terminals?.length ?? 0) <= 1
                            }
                            onClick={() => setDeleteTerminalConfirm(t)}
                          >
                            Удалить
                          </Button>
                        </div>
                        <div className={classes.deviceGrid3}>
                          <div>
                            <label className={classes.fieldLabelSmall}>
                              Наименование терминала
                            </label>
                            <input
                              className={classes.fieldInput}
                              value={terminalNames[t.orderUnitId] ?? ""}
                              onChange={(e) =>
                                setTerminalNames((s) => ({
                                  ...s,
                                  [t.orderUnitId]: e.target.value,
                                }))
                              }
                              placeholder="Название терминала"
                            />
                          </div>
                          <div>
                            <label className={classes.fieldLabelSmall}>
                              Ориентация терминала
                            </label>
                            <select
                              className={classes.fieldInput}
                              value={
                                terminalOrientations[t.orderUnitId] ??
                                ((t.terminalOrientation as
                                  | "VERTICAL"
                                  | "HORIZONTAL") ||
                                  "VERTICAL")
                              }
                              onChange={(e) =>
                                setTerminalOrientations((s) => ({
                                  ...s,
                                  [t.orderUnitId]:
                                    (e.target.value as
                                      | "VERTICAL"
                                      | "HORIZONTAL") || "VERTICAL",
                                }))
                              }
                            >
                              <option value="VERTICAL">Вертикальная</option>
                              <option value="HORIZONTAL">Горизонтальная</option>
                            </select>
                          </div>
                          <div>
                            <label className={classes.fieldLabelSmall}>
                              IP-адрес
                            </label>
                            <input
                              className={classes.fieldInput}
                              value={terminalIps[t.orderUnitId] ?? ""}
                              onChange={(e) => {
                                const next = e.target.value;
                                setTerminalIps((s) => ({
                                  ...s,
                                  [t.orderUnitId]: next,
                                }));
                                if (terminalIpErrors[t.orderUnitId]) {
                                  setTerminalIpErrors((s) => ({
                                    ...s,
                                    [t.orderUnitId]: null,
                                  }));
                                }
                              }}
                              placeholder="192.168.1.1 или 192.168.1.1:8081"
                            />
                            {terminalIpErrors[t.orderUnitId] && (
                              <div
                                className={classes.error}
                                style={{ marginTop: 6 }}
                              >
                                {terminalIpErrors[t.orderUnitId]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 8, opacity: 0.8 }}>
                    У очереди нет терминалов.
                  </div>
                )}
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>Табло очереди</div>
                {queueLinksLoading ? null : queueLinks?.tablos?.length ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {queueLinks.tablos.map((m) => (
                      <div key={m.monitorId} className={classes.deviceCard}>
                        <div className={classes.deviceCardHeader}>
                          <div className={classes.deviceCardTitle}>
                            {m.monitorName || "Табло"}
                          </div>
                          <Button
                            content="Удалить"
                            btnStyle="white"
                            className={classes.btnDangerSmall}
                            isDisabled={(queueLinks.tablos?.length ?? 0) <= 1}
                            onClick={() => setDeleteTabloConfirm(m)}
                          />
                        </div>
                        <div className={classes.deviceGrid2}>
                          <div>
                            <label className={classes.fieldLabelSmall}>
                              Наименование табло
                            </label>
                            <input
                              className={classes.fieldInput}
                              value={monitorNames[m.monitorId] ?? ""}
                              onChange={(e) =>
                                setMonitorNames((s) => ({
                                  ...s,
                                  [m.monitorId]: e.target.value,
                                }))
                              }
                              placeholder="Название табло"
                            />
                          </div>
                          <div>
                            <label className={classes.fieldLabelSmall}>
                              Ориентация
                            </label>
                            <select
                              className={classes.fieldInput}
                              value={
                                monitorOrientations[m.monitorId] ??
                                ((m.tabloOrientation as
                                  | "VERTICAL"
                                  | "HORIZONTAL") ||
                                  "HORIZONTAL")
                              }
                              onChange={(e) =>
                                setMonitorOrientations((s) => ({
                                  ...s,
                                  [m.monitorId]:
                                    (e.target.value as
                                      | "VERTICAL"
                                      | "HORIZONTAL") || "HORIZONTAL",
                                }))
                              }
                            >
                              <option value="VERTICAL">Вертикальная</option>
                              <option value="HORIZONTAL">Горизонтальная</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 8, opacity: 0.8 }}>
                    У очереди нет табло.
                  </div>
                )}
              </div>
            </div>

            {(saveError ||
              updateQueueMutation.error ||
              updateTerminalMutation.error ||
              updateMonitorMutation.error) && (
              <div className={classes.error}>
                {saveError ||
                  getMutationError(updateQueueMutation.error) ||
                  getMutationError(updateTerminalMutation.error) ||
                  getMutationError(updateMonitorMutation.error) ||
                  "Ошибка"}
              </div>
            )}
            <div
              className={`${classes.modalActions} ${classes.modalActionsSticky}`}
            >
              <Button
                content="Отмена"
                btnStyle="white"
                // className={classes.btnSecondary}
                onClick={onClose}
                isDisabled={
                  updateQueueMutation.isLoading ||
                  updateTerminalMutation.isLoading ||
                  updateMonitorMutation.isLoading
                }
              />
              <Button
                content="Сохранить"
                btnStyle="blue"
                // className={classes.btnPrimary}
                isLoading={
                  updateQueueMutation.isLoading ||
                  updateTerminalMutation.isLoading ||
                  updateMonitorMutation.isLoading
                }
                isDisabled={
                  updateQueueMutation.isLoading ||
                  updateTerminalMutation.isLoading ||
                  updateMonitorMutation.isLoading
                }
                onClick={() => formRef.current?.requestSubmit()}
              />
            </div>
          </form>
        </div>
      </div>

      {deleteTerminalConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteTerminalMutation as any).reset?.();
            setDeleteTerminalConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <h2 className={modalBase.title}>Удалить терминал?</h2>
            <p>
              Терминал «{deleteTerminalConfirm.terminalName || "Терминал"}»
              будет удалён из очереди. У очереди должен остаться хотя бы один
              терминал.
            </p>
            {deleteTerminalMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteTerminalMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <Button
                content="Отмена"
                btnStyle="white"
                // className={classes.btnSecondary}
                onClick={() => {
                  (deleteTerminalMutation as any).reset?.();
                  setDeleteTerminalConfirm(null);
                }}
                isDisabled={deleteTerminalMutation.isLoading}
              />
              <Button
                content="Удалить"
                btnStyle="white"
                // className={classes.btnDanger}
                isDeleteBtn
                isLoading={deleteTerminalMutation.isLoading}
                isDisabled={(queueLinks?.terminals?.length ?? 0) <= 1}
                onClick={() =>
                  deleteTerminalMutation.mutate({
                    orderUnitId: deleteTerminalConfirm.orderUnitId,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}

      {deleteTabloConfirm && (
        <div
          className={classes.modalOverlay}
          onClick={() => {
            (deleteTabloMutation as any).reset?.();
            setDeleteTabloConfirm(null);
          }}
        >
          <div
            className={classNames(modalBase.container, classes.modalBox)}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <h2 className={modalBase.title}>Удалить табло?</h2>
            <p>
              Табло «{deleteTabloConfirm.monitorName || "Табло"}» будет удалено
              из очереди. У очереди должно остаться хотя бы одно табло.
            </p>
            {deleteTabloMutation.error && (
              <div className={classes.error}>
                {getMutationError(deleteTabloMutation.error)}
              </div>
            )}
            <div className={classNames(modalBase.footer, classes.modalActions)}>
              <Button
                content="Отмена"
                btnStyle="white"
                // className={classes.btnSecondary}
                onClick={() => {
                  (deleteTabloMutation as any).reset?.();
                  setDeleteTabloConfirm(null);
                }}
                isDisabled={deleteTabloMutation.isLoading}
              />
              <Button
                content="Удалить"
                btnStyle="white"
                // className={classes.btnDanger}
                isDeleteBtn
                isLoading={deleteTabloMutation.isLoading}
                isDisabled={(queueLinks?.tablos?.length ?? 0) <= 1}
                onClick={() =>
                  deleteTabloMutation.mutate({
                    monitorId: deleteTabloConfirm.monitorId,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

type EditTerminalIpFormProps = {
  initialIp: string;
  onCancel: () => void;
  onSubmit: (ipAddress: string) => void;
  isLoading: boolean;
  error: string | false;
};

const EditTerminalIpForm: FC<EditTerminalIpFormProps> = ({
  initialIp,
  onCancel,
  onSubmit,
  isLoading,
  error,
}) => {
  const [ipAddress, setIpAddress] = useState(initialIp);
  const [ipValidationError, setIpValidationError] = useState<string | null>(
    null,
  );
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = ipAddress.trim();
    if (!isValidIpWithOptionalPort(trimmed)) {
      setIpValidationError(
        "Некорректный IP-адрес. Ожидается формат IPv4, опционально с портом.",
      );
      return;
    }
    setIpValidationError(null);
    onSubmit(trimmed);
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className={classes.section} style={{ marginTop: 0 }}>
        <div className={classes.deviceCard}>
          <label className={classes.fieldLabelSmall}>IP-адрес терминала</label>
          <input
            className={classes.fieldInput}
            value={ipAddress}
            onChange={(e) => {
              setIpAddress(e.target.value);
              if (ipValidationError) {
                setIpValidationError(null);
              }
            }}
            placeholder="192.168.1.1 или 192.168.1.1:8081"
          />
        </div>
      </div>
      {(ipValidationError || error) && (
        <div className={classes.error}>{ipValidationError || error}</div>
      )}
      <div className={classes.modalActions}>
        <Button
          type="button"
          // className={classes.btnSecondary}
          onClick={onCancel}
        >
          Отмена
        </Button>
        <Button
          type="submit"
          // className={classes.btnPrimary}
          disabled={isLoading}
        >
          {isLoading ? "Сохранение..." : "Сохранить"}
        </Button>
      </div>
    </form>
  );
};

type ModalAddTabloProps = {
  branchName: string;
  queueName: string;
  onClose: () => void;
  onSubmit: (body?: {
    monitorName?: string;
    tabloOrientation?: "VERTICAL" | "HORIZONTAL";
  }) => void;
  isLoading: boolean;
  result: QueueMonitorAddedDto | null;
  error: string | false;
};

const ModalAddTablo: FC<ModalAddTabloProps> = ({
  branchName,
  queueName,
  onClose,
  onSubmit,
  isLoading,
  result,
  error,
}) => {
  const [monitorName, setMonitorName] = useState("");
  const [monitorNameError, setMonitorNameError] = useState<string | null>(null);
  const [tabloOrientation, setTabloOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = monitorName.trim();
    if (!trimmedName) {
      setMonitorNameError("Введите название табло.");
      return;
    }
    setMonitorNameError(null);
    onSubmit({ monitorName: trimmedName, tabloOrientation });
  };

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div
        className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <h2 className={classes.modalTitle}>Добавить табло к очереди</h2>
        <p style={{ marginTop: 0 }}>
          {branchName} — {queueName}
        </p>
        {!result ? (
          <form onSubmit={handleSubmit} className={classes.modalFormScrollable}>
            <div className={classes.modalScrollArea}>
              <div className={classes.section} style={{ marginTop: 0 }}>
                <div className={classes.deviceCard}>
                  <div className={classes.deviceGrid2}>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Название табло
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!monitorNameError,
                        })}
                        value={monitorName}
                        onChange={(e) => {
                          setMonitorName(e.target.value);
                          if (monitorNameError) {
                            setMonitorNameError(null);
                          }
                        }}
                      />
                      {monitorNameError && (
                        <div className={classes.fieldError}>
                          {monitorNameError}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Ориентация табло
                      </label>
                      <select
                        className={classes.fieldInput}
                        value={tabloOrientation}
                        onChange={(e) =>
                          setTabloOrientation(
                            (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                              "HORIZONTAL",
                          )
                        }
                      >
                        <option value="VERTICAL">Вертикальная</option>
                        <option value="HORIZONTAL">Горизонтальная</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {!monitorNameError && error && (
                <div className={classes.error}>{error}</div>
              )}
            </div>

            <div className={classes.modalActionsSticky}>
              <div className={classes.modalActions}>
                <Button
                  type="button"
                  // className={classes.btnSecondary}
                  onClick={onClose}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  // className={classes.btnPrimary}
                  disabled={isLoading}
                >
                  {isLoading ? "Добавление..." : "Добавить табло"}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <>
            <p>Табло «{result.monitorName}» добавлено. Ссылка для доступа:</p>
            <div className={classes.urlsBlock}>
              <p>
                <strong>Табло:</strong>
                <br />
                <a
                  href={toFrontendLink(result.tabloUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(result.tabloUrl)}
                </a>
              </p>
              <p>
                <strong>Token (табло):</strong>
                <br />
                {result.monitorToken}
              </p>
            </div>
            <div className={classes.modalActions} style={{ marginTop: 16 }}>
              <Button type="button" btnStyle="blue" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

type ModalAddTerminalProps = {
  branchName: string;
  queueName: string;
  onClose: () => void;
  onSubmit: (body?: { terminalName?: string; ipAddress?: string }) => void;
  isLoading: boolean;
  result: QueueTerminalAddedDto | null;
  error: string | false;
};

const ModalAddTerminal: FC<ModalAddTerminalProps> = ({
  branchName,
  queueName,
  onClose,
  onSubmit,
  isLoading,
  result,
  error,
}) => {
  const [terminalName, setTerminalName] = useState("");
  const [terminalNameError, setTerminalNameError] = useState<string | null>(
    null,
  );
  const [ipAddress, setIpAddress] = useState("");
  const [ipValidationError, setIpValidationError] = useState<string | null>(
    null,
  );
  const [terminalOrientation, setTerminalOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTerminalName = terminalName.trim();
    const trimmedIp = ipAddress.trim();

    if (!trimmedTerminalName) {
      setTerminalNameError("Введите название терминала.");
      setIpValidationError(null);
      return;
    }

    if (!trimmedIp) {
      setTerminalNameError(null);
      setIpValidationError("Введите IP-адрес терминала.");
      return;
    }

    if (!isValidIpWithOptionalPort(trimmedIp)) {
      setTerminalNameError(null);
      setIpValidationError(
        "Некорректный IP-адрес. Ожидается формат IPv4, опционально с портом.",
      );
      return;
    }

    setTerminalNameError(null);
    setIpValidationError(null);
    onSubmit({
      terminalName: trimmedTerminalName,
      ipAddress: trimmedIp,
    });
  };

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div
        className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <h2 className={classes.modalTitle}>Добавить терминал к очереди</h2>
        <p style={{ marginTop: 0 }}>
          {branchName} — {queueName}
        </p>
        {!result ? (
          <form onSubmit={handleSubmit} className={classes.modalFormScrollable}>
            <div className={classes.modalScrollArea}>
              <div className={classes.section} style={{ marginTop: 0 }}>
                <div className={classes.deviceCard}>
                  <div className={classes.deviceGrid3}>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Название терминала
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!terminalNameError,
                        })}
                        value={terminalName}
                        onChange={(e) => {
                          setTerminalName(e.target.value);
                          if (terminalNameError) {
                            setTerminalNameError(null);
                          }
                        }}
                        placeholder="по умолчанию: Терминал 2, Терминал 3…"
                      />
                      {terminalNameError && (
                        <div className={classes.fieldError}>
                          {terminalNameError}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Ориентация терминала
                      </label>
                      <select
                        className={classes.fieldInput}
                        value={terminalOrientation}
                        onChange={(e) =>
                          setTerminalOrientation(
                            (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                              "HORIZONTAL",
                          )
                        }
                      >
                        <option value="VERTICAL">Вертикальная</option>
                        <option value="HORIZONTAL">Горизонтальная</option>
                      </select>
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        IP-адрес терминала
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!ipValidationError,
                        })}
                        value={ipAddress}
                        onChange={(e) => {
                          setIpAddress(e.target.value);
                          if (ipValidationError) {
                            setIpValidationError(null);
                          }
                        }}
                        placeholder="192.168.1.1 или 192.168.1.1:8081"
                      />
                      {ipValidationError && (
                        <div className={classes.fieldError}>
                          {ipValidationError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {error && <div className={classes.error}>{error}</div>}
            </div>

            <div className={classes.modalActionsSticky}>
              <div className={classes.modalActions}>
                <Button
                  content="Отмена"
                  btnStyle="white"
                  onClick={onClose}
                  disabled={isLoading}
                  type="button"
                />
                <Button
                  content="Добавить"
                  btnStyle="blue"
                  type="submit"
                  isLoading={isLoading}
                />
              </div>
            </div>
          </form>
        ) : (
          <>
            <p>
              Терминал «{result.terminalName}» добавлен. Ссылка для доступа:
            </p>
            <div className={classes.urlsBlock}>
              <p>
                <strong>Терминал:</strong>
                <br />
                <a
                  href={toFrontendLink(result.terminalUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(result.terminalUrl)}
                </a>
              </p>
              <p>
                <strong>Token (терминал):</strong>
                <br />
                {result.terminalToken}
              </p>
            </div>
            <div className={classes.modalActions} style={{ marginTop: 16 }}>
              <button
                type="button"
                className={classes.btnPrimary}
                onClick={onClose}
              >
                Закрыть
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

type ModalCreateQueueProps = {
  branchId: number;
  branchName: string;
  onClose: () => void;
  onSubmit: (body: {
    queueName?: string;
    terminalName?: string;
    monitorName?: string;
    ipAddress?: string;
    terminalOrientation?: "VERTICAL" | "HORIZONTAL";
    tabloOrientation?: "VERTICAL" | "HORIZONTAL";
  }) => void;
  isLoading: boolean;
  queueResult: BranchQueueCreatedDto | null;
  error: string | false;
};

const ModalCreateQueue: FC<ModalCreateQueueProps> = ({
  branchId,
  branchName,
  onClose,
  onSubmit,
  isLoading,
  queueResult,
  error,
}) => {
  const [queueName, setQueueName] = useState("");
  const [terminalName, setTerminalName] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [monitorName, setMonitorName] = useState("");
  const [terminalOrientation, setTerminalOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [tabloOrientation, setTabloOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [queueNameError, setQueueNameError] = useState<string | null>(null);
  const [terminalNameError, setTerminalNameError] = useState<string | null>(
    null,
  );
  const [ipAddressError, setIpAddressError] = useState<string | null>(null);
  const [monitorNameError, setMonitorNameError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQueueName = queueName.trim();
    const trimmedTerminalName = terminalName.trim();
    const trimmedIpAddress = ipAddress.trim();
    const trimmedMonitorName = monitorName.trim();

    if (!trimmedQueueName) {
      setQueueNameError("Введите название очереди.");
      setTerminalNameError(null);
      setIpAddressError(null);
      setMonitorNameError(null);
      setValidationError(null);
      return;
    }

    if (!trimmedTerminalName) {
      setQueueNameError(null);
      setTerminalNameError("Введите название терминала.");
      setIpAddressError(null);
      setMonitorNameError(null);
      setValidationError(null);
      return;
    }

    if (!trimmedIpAddress) {
      setQueueNameError(null);
      setTerminalNameError(null);
      setIpAddressError("Введите IP-адрес терминала.");
      setMonitorNameError(null);
      setValidationError(null);
      return;
    }

    if (!isValidIpWithOptionalPort(trimmedIpAddress)) {
      setQueueNameError(null);
      setTerminalNameError(null);
      setIpAddressError(
        "Некорректный IP-адрес. Ожидается формат IPv4, опционально с портом.",
      );
      setMonitorNameError(null);
      setValidationError(null);
      return;
    }

    if (!trimmedMonitorName) {
      setQueueNameError(null);
      setTerminalNameError(null);
      setIpAddressError(null);
      setMonitorNameError("Введите название табло.");
      setValidationError(null);
      return;
    }

    setQueueNameError(null);
    setTerminalNameError(null);
    setIpAddressError(null);
    setMonitorNameError(null);
    setValidationError(null);
    onSubmit({
      queueName: queueName.trim() || undefined,
      terminalName: trimmedTerminalName || undefined,
      ipAddress: trimmedIpAddress || undefined,
      monitorName: trimmedMonitorName || undefined,
      terminalOrientation,
      tabloOrientation,
    });
  };

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div
        className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <h2 className={classes.modalTitle}>Создать очередь — {branchName}</h2>
        {!queueResult ? (
          <form
            onSubmit={handleSubmit}
            className={classes.modalFormScrollable}
            noValidate
          >
            <div className={classes.modalScrollArea}>
              <div className={classes.formGroup} style={{ marginBottom: 0 }}>
                <label>Название очереди</label>
                <input
                  value={queueName}
                  onChange={(e) => {
                    setQueueName(e.target.value);
                    if (queueNameError) setQueueNameError(null);
                  }}
                  placeholder="например: Окно 1"
                  className={classNames({
                    [classes.fieldInputError]: !!queueNameError,
                  })}
                />
                {queueNameError && (
                  <div className={classes.fieldError}>{queueNameError}</div>
                )}
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>Терминал очереди</div>
                <div className={classes.deviceCard}>
                  <div className={classes.deviceGrid3}>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Наименование терминала
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!terminalNameError,
                        })}
                        value={terminalName}
                        onChange={(e) => {
                          setTerminalName(e.target.value);
                          if (terminalNameError) setTerminalNameError(null);
                        }}
                        placeholder="например: Терминал 1"
                      />
                      {terminalNameError && (
                        <div className={classes.fieldError}>
                          {terminalNameError}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        IP-адрес терминала
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!ipAddressError,
                        })}
                        value={ipAddress}
                        onChange={(e) => {
                          setIpAddress(e.target.value);
                          if (ipAddressError) setIpAddressError(null);
                        }}
                        placeholder="например: 192.168.1.10:8080"
                      />
                      {ipAddressError && (
                        <div className={classes.fieldError}>
                          {ipAddressError}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Ориентация терминала
                      </label>
                      <select
                        className={classes.fieldInput}
                        value={terminalOrientation}
                        onChange={(e) =>
                          setTerminalOrientation(
                            (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                              "HORIZONTAL",
                          )
                        }
                      >
                        <option value="VERTICAL">Вертикальная</option>
                        <option value="HORIZONTAL">Горизонтальная</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>Табло очереди</div>
                <div className={classes.deviceCard}>
                  <div className={classes.deviceGrid2}>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Наименование табло
                      </label>
                      <input
                        className={classNames(classes.fieldInput, {
                          [classes.fieldInputError]: !!monitorNameError,
                        })}
                        value={monitorName}
                        onChange={(e) => {
                          setMonitorName(e.target.value);
                          if (monitorNameError) setMonitorNameError(null);
                        }}
                        placeholder="например: Табло 1"
                      />
                      {monitorNameError && (
                        <div className={classes.fieldError}>
                          {monitorNameError}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={classes.fieldLabelSmall}>
                        Ориентация
                      </label>
                      <select
                        className={classes.fieldInput}
                        value={tabloOrientation}
                        onChange={(e) =>
                          setTabloOrientation(
                            (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                              "HORIZONTAL",
                          )
                        }
                      >
                        <option value="VERTICAL">Вертикальная</option>
                        <option value="HORIZONTAL">Горизонтальная</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {(validationError || error) && (
                <div className={classes.error}>{validationError || error}</div>
              )}
            </div>

            <div className={classes.modalActionsSticky}>
              <div className={classes.modalActions}>
                <Button type="button" btnStyle="white" onClick={onClose}>
                  Отмена
                </Button>
                <Button type="submit" btnStyle="blue" isLoading={isLoading}>
                  {isLoading ? "Создание..." : "Создать"}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <>
            <p>Очередь создана. Ссылки для доступа:</p>
            <div className={classes.urlsBlock}>
              <p>
                <strong>Терминал:</strong>
                <br />
                <a
                  href={toFrontendLink(queueResult.terminalUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(queueResult.terminalUrl)}
                </a>
              </p>
              <p>
                <strong>Табло:</strong>
                <br />
                <a
                  href={toFrontendLink(queueResult.tabloUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(queueResult.tabloUrl)}
                </a>
              </p>
              <p>
                <strong>Оператор:</strong>
                <br />
                <a
                  href={toFrontendLink(queueResult.operatorUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(queueResult.operatorUrl)}
                </a>
              </p>
              <p>
                <strong>Админка:</strong>
                <br />
                <a
                  href={toFrontendLink(queueResult.adminUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {toFrontendLink(queueResult.adminUrl)}
                </a>
              </p>
              <p>
                <strong>Token (терминал):</strong>
                <br />
                {queueResult.terminalToken}
              </p>
              <p>
                <strong>Token (табло):</strong>
                <br />
                {queueResult.monitorToken}
              </p>
            </div>
            <div className={classes.modalActions} style={{ marginTop: 16 }}>
              <button
                type="button"
                className={classes.btnPrimary}
                onClick={onClose}
              >
                Закрыть
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

type BindingItem = { id: number; nameRu: string };

function flattenCategories(
  categories: { id: number; nameRu: string; subCategories?: unknown[] }[],
): BindingItem[] {
  const out: BindingItem[] = [];
  function walk(
    list: { id: number; nameRu: string; subCategories?: unknown[] }[],
  ) {
    list.forEach((c) => {
      out.push({ id: c.id, nameRu: c.nameRu });
      if (c.subCategories && c.subCategories.length)
        walk(
          c.subCategories as {
            id: number;
            nameRu: string;
            subCategories?: unknown[];
          }[],
        );
    });
  }
  walk(categories);
  return out;
}

type TopicLike = {
  id: number;
  nameRu: string;
  /** признак логического удаления (разные формы от API) */
  isDeleted?: boolean;
  is_deleted?: boolean;
  queues?: { id: number }[];
  queueIds?: number[];
};

function flattenTopics(
  categories: {
    topics?: TopicLike[];
    subCategories?: unknown[];
  }[],
  unusedTopics: TopicLike[],
  queueId?: number,
): BindingItem[] {
  const out: BindingItem[] = [];
  const isTopicDeleted = (t: TopicLike) =>
    t.isDeleted === true || t.is_deleted === true;
  function walk(
    list: {
      topics?: TopicLike[];
      subCategories?: unknown[];
    }[],
  ) {
    list.forEach((c) => {
      if (c.topics) {
        c.topics.forEach((t) => {
          // Удалённые услуги не должны появляться в привязках табло/терминала
          if (isTopicDeleted(t)) return;
          const topicQueueIds = [
            ...(t.queues?.map((q) => q.id) ?? []),
            ...(t.queueIds ?? []),
          ];
          const allowedForQueue =
            !queueId ||
            topicQueueIds.length === 0 ||
            topicQueueIds.includes(queueId as number);
          if (allowedForQueue) {
            out.push({ id: t.id, nameRu: t.nameRu });
          }
        });
      }
      if (c.subCategories && c.subCategories.length)
        walk(
          c.subCategories as {
            topics?: TopicLike[];
            subCategories?: unknown[];
          }[],
        );
    });
  }
  walk(categories);
  (unusedTopics || []).forEach((t) => {
    if (isTopicDeleted(t)) return;
    const topicQueueIds = [
      ...(t.queues?.map((q) => q.id) ?? []),
      ...(t.queueIds ?? []),
    ];
    const allowedForQueue =
      !queueId ||
      topicQueueIds.length === 0 ||
      topicQueueIds.includes(queueId as number);
    if (allowedForQueue) {
      out.push({ id: t.id, nameRu: t.nameRu });
    }
  });
  return out;
}

type ModalQueueBindingProps = {
  branchId: number;
  branchCode: string;
  queueId: number;
  type: "categories" | "topics" | "monitor-topics";
  title: string;
  onClose: () => void;
  onSaved: () => void;
};

type QueueMonitorItem = { monitorId: number; monitorName: string };

const ModalQueueBinding: FC<ModalQueueBindingProps> = ({
  branchId,
  branchCode,
  queueId,
  type,
  title,
  onClose,
  onSaved,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [applyToAllTablos, setApplyToAllTablos] = useState(false);
  const [selectedMonitorId, setSelectedMonitorId] = useState<number | null>(
    null,
  );

  const [applyToAllTerminals, setApplyToAllTerminals] = useState(false);
  const [selectedTerminalId, setSelectedTerminalId] = useState<number | null>(
    null,
  );
  // Для модалки "Услуги" нужно хранить локальные чекбоксы отдельно для каждого терминала,
  // чтобы при переключении `selectedTerminalId` не терялись уже сделанные правки.
  const [selectedIdsByTerminal, setSelectedIdsByTerminal] = useState<
    Record<number, number[]>
  >({});
  // Флаг "грязности" нужен, чтобы при повторных загрузках с сервера (при переключении)
  // не перезаписывать локально измененный терминал.
  const [dirtyByTerminal, setDirtyByTerminal] = useState<
    Record<number, boolean>
  >({});

  const { data: queueMonitors = [] } = useQuery<QueueMonitorItem[]>(
    ["queueMonitors", branchId, queueId],
    () =>
      axios
        .get(BranchAPI.getQueueMonitors(branchId, queueId), commonHeaders())
        .then((r) => r.data),
    { enabled: type === "monitor-topics" && !!branchId && !!queueId },
  );

  const { data: queueLinksForTerminals } = useQuery<BranchQueueLinksDto>(
    ["queueLinksForBindingTerminals", branchId, queueId],
    () =>
      axios
        .get(BranchAPI.queueLinks(branchId, queueId), commonHeaders())
        .then((r) => r.data),
    { enabled: type === "topics" && !!branchId && !!queueId },
  );

  const queueTerminals = queueLinksForTerminals?.terminals ?? [];

  React.useEffect(() => {
    if (
      type === "monitor-topics" &&
      queueMonitors.length > 0 &&
      selectedMonitorId == null
    ) {
      setSelectedMonitorId(queueMonitors[0].monitorId);
    }
  }, [type, queueMonitors, selectedMonitorId]);

  React.useEffect(() => {
    if (type !== "topics") return;
    if (!queueTerminals.length) return;

    const firstId = queueTerminals[0].orderUnitId;
    if (selectedTerminalId == null) {
      setSelectedTerminalId(firstId);
      return;
    }

    const exists = queueTerminals.some(
      (t) => t.orderUnitId === selectedTerminalId,
    );
    if (!exists) setSelectedTerminalId(firstId);
  }, [type, queueTerminals, selectedTerminalId]);

  // Чтобы при переключении терминала чекбоксы не оставались от предыдущего набора,
  // применяем только сброс флага "применить ко всем", но сохраняем локальные чекбоксы
  // для каждого терминала.
  React.useEffect(() => {
    if (type !== "topics") return;
    setApplyToAllTerminals(false);
  }, [type, selectedTerminalId]);

  const getBindingUrl = () => {
    if (type === "categories")
      return BranchAPI.getQueueCategories(branchId, queueId);
    if (type === "topics") {
      if (selectedTerminalId != null) {
        return BranchAPI.getQueueTerminalTopicsByTerminal(
          branchId,
          queueId,
          selectedTerminalId,
        );
      }
      return BranchAPI.getQueueTopics(branchId, queueId);
    }
    if (type === "monitor-topics" && selectedMonitorId != null) {
      return BranchAPI.getQueueMonitorTopicsByMonitor(
        branchId,
        queueId,
        selectedMonitorId,
      );
    }
    return BranchAPI.getQueueMonitorTopics(branchId, queueId);
  };
  const getSetBindingUrl = (): string => {
    if (type === "categories")
      return BranchAPI.setQueueCategories(branchId, queueId);
    if (type === "topics") {
      if (applyToAllTerminals) {
        return BranchAPI.setQueueTerminalTopicsAll(branchId, queueId);
      }
      if (selectedTerminalId != null) {
        return BranchAPI.setQueueTerminalTopicsByTerminal(
          branchId,
          queueId,
          selectedTerminalId,
        );
      }
      return BranchAPI.setQueueTopics(branchId, queueId);
    }
    if (type === "monitor-topics" && applyToAllTablos)
      return BranchAPI.setQueueMonitorTopicsAll(branchId, queueId);
    if (type === "monitor-topics" && selectedMonitorId != null) {
      return BranchAPI.setQueueMonitorTopicsByMonitor(
        branchId,
        queueId,
        selectedMonitorId,
      );
    }
    return BranchAPI.setQueueMonitorTopics(branchId, queueId);
  };

  const { data: bindingData, isLoading: loadingBinding } = useQuery<{
    ids: number[];
  }>(
    [
      "queueBinding",
      branchId,
      queueId,
      type,
      type === "monitor-topics" ? selectedMonitorId : undefined,
      type === "topics" ? selectedTerminalId : undefined,
    ],
    () => axios.get(getBindingUrl(), commonHeaders()).then((r) => r.data),
    {
      enabled:
        !!branchId &&
        !!queueId &&
        (type !== "monitor-topics" || selectedMonitorId != null) &&
        (type !== "topics" || selectedTerminalId != null),
    },
  );

  const { data: viewData } = useQuery<{
    categories: {
      id: number;
      nameRu: string;
      subCategories?: unknown[];
      topics?: {
        id: number;
        nameRu: string;
        isDeleted?: boolean;
        is_deleted?: boolean;
      }[];
    }[];
    unusedTopics: {
      id: number;
      nameRu: string;
      isDeleted?: boolean;
      is_deleted?: boolean;
    }[];
  }>(
    ["categoriesTopicsView", branchId],
    () =>
      axios
        .get(CategoriesTopicsTableAPI.getAllCategoriesTopics, {
          ...commonHeaders(),
          params: { branchId },
        })
        .then((r) => r.data),
    {
      enabled:
        (type === "categories" ||
          type === "topics" ||
          type === "monitor-topics") &&
        !!branchId,
    },
  );

  const { data: queueTopicsData } = useQuery<{ ids: number[] }>(
    ["queueTopics", branchId, queueId],
    () => {
      return axios
        .get(BranchAPI.getQueueTopics(branchId, queueId), commonHeaders())
        .then((r) => r.data);
    },
    {
      enabled:
        (type === "monitor-topics" ||
          type === "topics" ||
          type === "categories") &&
        !!branchId &&
        !!queueId,
    },
  );

  // Для модалки "Услуги" нужно показывать и активные, и неактивные услуги.
  // `flattenTopics(...)` строит список кандидатов через `/categories-topics/view`,
  // поэтому на всякий случай дополним список услуг, которые уже привязаны к очереди
  // (через queueTopicsData.ids), если их нет в candidates.
  const { data: allTopicsForQueueBinding } = useQuery<
    {
      id: number;
      nameRu: string;
      isDeleted?: boolean;
      is_deleted?: boolean;
    }[]
  >(
    ["allTopicsForQueueBinding", branchId],
    () =>
      axios
        .get(AdminAPI.getAllSystemTopics, {
          ...commonHeaders(),
          params: { branch: branchCode },
        })
        .then((r) => r.data),
    {
      enabled: (type === "topics" || type === "monitor-topics") && !!branchId,
    },
  );

  const selectedIdsForRender =
    type === "topics"
      ? selectedTerminalId != null
        ? (selectedIdsByTerminal[selectedTerminalId] ?? [])
        : []
      : selectedIds;

  const items = useMemo(() => {
    if (type === "categories") {
      if (!viewData?.categories) return [];
      const all = flattenCategories(viewData.categories);
      const allowed = new Set(selectedIdsForRender);
      return all.filter((c) => allowed.has(c.id));
    }

    if (type === "topics" || type === "monitor-topics") {
      const topicIds = queueTopicsData?.ids ?? [];

      const candidates = flattenTopics(
        viewData?.categories ?? [],
        viewData?.unusedTopics ?? [],
        queueId,
      );

      const lookup = new Map(
        (allTopicsForQueueBinding ?? [])
          .filter((t) => !(t.isDeleted === true || t.is_deleted === true))
          .map((t) => [t.id, t.nameRu] as const),
      );

      const candidatesMap = new Map<number, BindingItem>();
      candidates.forEach((c) => candidatesMap.set(c.id, c));

      // Если услуга уже привязана, но не попала в candidates, всё равно покажем.
      topicIds.forEach((id) => {
        if (!candidatesMap.has(id)) {
          const name = lookup.get(id);
          // Если услуга удалена — не показываем (требование: не отображать удалённые услуги)
          if (name == null) return;
          candidatesMap.set(id, {
            id,
            nameRu: name && name.trim().length ? name : `Услуга ${id}`,
          });
        }
      });

      const mapped = Array.from(candidatesMap.values()).sort((a, b) =>
        a.nameRu.localeCompare(b.nameRu, "ru"),
      );

      return mapped;
    }

    return [];
  }, [
    type,
    viewData,
    queueTopicsData,
    queueId,
    allTopicsForQueueBinding,
    selectedIdsForRender,
  ]);

  React.useEffect(() => {
    if (!bindingData?.ids) return;

    // Для "Услуги" обновляем локальное состояние только если терминал не трогали вручную.
    if (type === "topics" && selectedTerminalId != null) {
      if (dirtyByTerminal[selectedTerminalId]) return;
      setSelectedIdsByTerminal((prev) => ({
        ...prev,
        [selectedTerminalId]: bindingData.ids,
      }));
      return;
    }

    setSelectedIds(bindingData.ids);
  }, [bindingData?.ids, type, selectedTerminalId, dirtyByTerminal]);

  const toggle = (id: number) => {
    if (type === "topics" && selectedTerminalId != null) {
      setSelectedIdsByTerminal((prev) => {
        const terminalIds = prev[selectedTerminalId] ?? [];
        const nextIds = terminalIds.includes(id)
          ? terminalIds.filter((x) => x !== id)
          : [...terminalIds, id];

        return {
          ...prev,
          [selectedTerminalId]: nextIds,
        };
      });
      setDirtyByTerminal((prev) => ({
        ...prev,
        [selectedTerminalId]: true,
      }));
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const saveMutation = useMutation(
    async () => {
      if (type !== "topics") {
        return axios.put(
          getSetBindingUrl(),
          { ids: selectedIds },
          commonHeaders(),
        );
      }

      // "Применить ко всем терминалам" - делаем единичное обновление всего набора.
      if (applyToAllTerminals) {
        return axios.put(
          getSetBindingUrl(),
          { ids: selectedIdsForRender },
          commonHeaders(),
        );
      }

      // Иначе: сохраняем привязки последовательно для всех терминалов очереди.
      for (const t of queueTerminals) {
        const terminalId = t.orderUnitId;

        let ids = selectedIdsByTerminal[terminalId];
        const isDirty = dirtyByTerminal[terminalId] === true;

        // Если терминал ещё не загружали/не трогали локально — сначала подтянем текущую привязку,
        // чтобы не "обнулить" его случайно.
        if (ids == null && !isDirty) {
          const r = await axios.get(
            BranchAPI.getQueueTerminalTopicsByTerminal(
              branchId,
              queueId,
              terminalId,
            ),
            commonHeaders(),
          );
          ids = r.data?.ids ?? [];
        }

        await axios.put(
          BranchAPI.setQueueTerminalTopicsByTerminal(
            branchId,
            queueId,
            terminalId,
          ),
          { ids: ids ?? [] },
          commonHeaders(),
        );
      }
    },
    {
      onSuccess: () => onSaved(),
      onError: () => {},
    },
  );

  const loading = loadingBinding;
  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div
        className={`${classes.modalBox} ${classes.modalBoxScrollable}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <h2 className={classes.modalTitle}>{title}</h2>
        {loading ? (
          <div style={{ padding: 16 }}>Загрузка...</div>
        ) : (
          <div className={classes.modalFormScrollable}>
            <div className={classes.modalScrollArea}>
              {type === "topics" && queueTerminals.length > 0 && (
                <div className={classes.section} style={{ marginTop: 0 }}>
                  <div className={classes.deviceCard}>
                    <label className={classes.fieldLabelSmall}>Терминал</label>
                    <select
                      className={classes.fieldInput}
                      value={selectedTerminalId ?? ""}
                      onChange={(e) =>
                        (() => {
                          const v = Number(e.target.value);
                          setSelectedTerminalId(
                            Number.isNaN(v) ? selectedTerminalId : v,
                          );
                        })()
                      }
                    >
                      {queueTerminals.map((t) => (
                        <option key={t.orderUnitId} value={t.orderUnitId}>
                          {t.terminalName || `Терминал ${t.orderUnitId}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {type === "monitor-topics" && queueMonitors.length > 0 && (
                <div className={classes.section} style={{ marginTop: 0 }}>
                  <div className={classes.deviceCard}>
                    <label className={classes.fieldLabelSmall}>Табло</label>
                    <select
                      className={classes.fieldInput}
                      value={selectedMonitorId ?? ""}
                      onChange={(e) =>
                        setSelectedMonitorId(Number(e.target.value) || null)
                      }
                    >
                      {queueMonitors.map((m) => (
                        <option key={m.monitorId} value={m.monitorId}>
                          {m.monitorName || `Табло ${m.monitorId}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className={classes.section} style={{ marginTop: 0 }}>
                <div className={classes.deviceCard}>
                  {items.length === 0 ? (
                    <p className={classes.queuesEmpty} style={{ padding: 0 }}>
                      {type === "monitor-topics"
                        ? "Сначала добавьте услуги в очередь."
                        : "Нет данных."}
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {items.map((item) => (
                        <label
                          key={item.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIdsForRender.includes(item.id)}
                            onChange={() => toggle(item.id)}
                          />
                          <span>{item.nameRu}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {type === "monitor-topics" && items.length > 0 && (
                <div className={classes.section} style={{ marginTop: 12 }}>
                  <div className={classes.deviceCard}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={applyToAllTablos}
                        onChange={(e) => setApplyToAllTablos(e.target.checked)}
                      />
                      <span>Применить ко всем табло данной очереди</span>
                    </label>
                  </div>
                </div>
              )}

              {type === "topics" &&
                items.length > 0 &&
                queueTerminals.length > 0 && (
                  <div className={classes.section} style={{ marginTop: 12 }}>
                    <div className={classes.deviceCard}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={applyToAllTerminals}
                          onChange={(e) =>
                            setApplyToAllTerminals(e.target.checked)
                          }
                        />
                        <span>Применить ко всем терминалам данной очереди</span>
                      </label>
                    </div>
                  </div>
                )}

              {saveMutation.error && (
                <div className={classes.error}>
                  {axios.isAxiosError(saveMutation.error)
                    ? saveMutation.error.response?.data?.message ||
                      saveMutation.error.message
                    : "Ошибка"}
                </div>
              )}
            </div>

            <div className={classes.modalActionsSticky}>
              <div
                className={classNames(modalBase.footer, classes.modalActions)}
                style={{ borderTop: "none" }}
              >
                <Button
                  content={"отмена"}
                  btnStyle={"white"}
                  onClick={onClose}
                />
                <Button
                  content={
                    saveMutation.isLoading ? "сохранение..." : "сохранить"
                  }
                  btnStyle={"blue"}
                  disabled={saveMutation.isLoading || items.length === 0}
                  onClick={() => saveMutation.mutate()}
                  isLoading={saveMutation.isLoading}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
